import { useEffect, useState } from 'react'
import { useParams,useSearchParams } from 'react-router-dom'
import { getCreditById } from '../graphql/queries'
import {
Box,
Typography,
TextField,
Snackbar,
Alert,
Button,
Dialog,
DialogActions,
DialogContent,
DialogTitle
} from '@mui/material'
import { useQuery, useMutation } from '@apollo/client'
import {updateOneCredit,deleteCredit} from '../graphql/mutation'
import { useNavigate } from 'react-router-dom';
import { toLocalISOString } from '../utils/dateUtils';
import { getMutationOptions } from '../utils/apolloHelpers';

export default function CreditDetailPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [openDialog,setOpenDialog] = useState(false)
    const [progress,setProgress] = useState(false)
    const [snackBarOpen,setSnackBarOpen] = useState(false)
    const [snackBarMessage,setSnackBarMessge] = useState(null)
    const [snackBarSeverity, setSnackBarSeverity] = useState('success')

    const[credit, setCredit] = useState(
        {
            amount: 0,
            expiryDate: '',
            id: '',
            date: '',   
            createdAt: '',
            updatedAt: '',
            rcEventId: '',
            productName: '',
            productType: '',
            transactionId: '',  
            isPromo: false,
            user: {
                id: ''
            }
        }
    )

    const [creditData, setCreditData] = useState(null)

    useEffect(() => {
        if(creditData){
            setCredit({
                amount: creditData.amount || 0,
                expiryDate: toLocalISOString(creditData.expiryDate),

                id: creditData.id || '',
                date: creditData.date || '',
                createdAt: creditData.createdAt || '',
                updatedAt: creditData.updatedAt || '',
                rcEventId: creditData.rcEventId || '',
                productName: creditData.productName || '',
                productType: creditData.productType || '',
                transactionId: creditData.transactionId || '',
                isPromo: creditData.isPromo || false,
                user: {
                    id: creditData.user?.id || ''
                }
            })
        }
    }, [creditData])

        const { data } = useQuery(getCreditById, {
        variables: { creditId: id }
        })

        useEffect(() => {
        if (data?.getCreditById) {
            setCreditData(data.getCreditById)
        }
        }, [data]) 

    const [updateOneCreditMutation] = useMutation(updateOneCredit)
    const [deleteCreditMutation] = useMutation(deleteCredit)

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCredit(prevCredit => ({
            ...prevCredit,
            [name]: value
        }));
    }

    const handleUpdateCredit = async () => {
        setProgress(true)

        try{
            const response = await updateOneCreditMutation({
                variables: {
                    creditId: credit.id,
                    amount: Number(credit.amount),
                    expiryDate: new Date(credit.expiryDate).toISOString()
                },
                // Properly refetch related queries
                ...getMutationOptions('CREDITS', credit.id)
            })
        if (response.data) {
        setSnackBarMessge('Credit updated successfully');
        setSnackBarSeverity('success');
        setSnackBarOpen(true);
        setTimeout(() => {
            navigate(`/users/${credit.user.id}`, { 
                state: { 
                    refreshData: true,
                    updatedCreditId: credit.id 
                }
            });
        }, 1000);
        }
        else {
        setSnackBarMessge('Credit update failed')
        setSnackBarSeverity('error')
        setSnackBarOpen(true)
    }

    } catch (error) {
    const message = error?.graphQLErrors?.[0]?.message || 'An unexpected error occurred';
    setSnackBarMessge(message);
    setSnackBarSeverity('error');
    setSnackBarOpen(true);
    }

    finally{
        setProgress(false)
    }
}

    const handleDeleteCredit = async () => {
        setProgress(true)
        try{
            const response = await deleteCreditMutation({
                variables: {
                    creditId: credit.id,
                },
                // Properly refetch related queries
                ...getMutationOptions('CREDITS', credit.id)
            })
    console.log("delete response", response);
    if(response.data){
        setSnackBarMessge('Credit deleted successfully')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
            setTimeout(() => {
            navigate(`/users/${credit.user.id}`) 
            }, 1000);
    } else {
        setSnackBarMessge('Credit delete failed')
        setSnackBarSeverity('error')
        setSnackBarOpen(true)
    }

    } catch(error){
        setSnackBarMessge('An error occurred while deleting the credit')
        setSnackBarSeverity('error') 
        setSnackBarOpen(true)
    }
    finally{
        setProgress(false)
    }
}


    return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 4 }}>
        <Snackbar
            open={snackBarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackBarOpen(false)}
            message={snackBarMessage}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert 
                onClose={() => setSnackBarOpen(false)} 
                severity={snackBarSeverity} 
                sx={{ width: '100%' }}
            >
                {snackBarMessage}
                </Alert>
        </Snackbar>

        <Typography variant="h4" fontWeight="bold" gutterBottom>
        Credit Details
        </Typography>

        <Box sx={{ p: 3, borderRadius: 3, backgroundColor: '#f7f7f9', width: '100%' }}>
        {[
            ['Credit ID', credit?.id || ''],
            ['Date', new Date(credit?.date).toLocaleString()],
            ['Created At', new Date(credit?.createdAt).toLocaleString()],
            ['Updated At', new Date(credit?.updatedAt).toLocaleString()],
            ['RC Event ID', credit?.rcEventId || '-'],
            ['Product Name', credit?.productName || '-'],
            ['Product Type', credit?.productType || '-'],
            ['Transaction ID', credit?.transactionId || '-'],
            ['Is Promo?', credit?.isPromo ? 'Yes' : 'No'],
            ['User ID', credit?.user?.id || '-'],
        ].map(([label, value], i) => (
            <TextField sx={{p:2, width: '50%'}}
            label={label}   
            value={value ||"-"}
            key={`readonly-${label}`}
            readOnly
            InputProps={{ readOnly: true }}
            variant="outlined"
            >
            </TextField>
        ))}
            {[
            ['Amount', 'amount', credit.amount, 'number'],
            ['Expiry Date', 'expiryDate', credit.expiryDate || '', 'datetime-local'],
            ].map(([label, name, value, type], i) => (
            <TextField
                key={`editable-${name}`}
                sx={{ p: 2, width: '50%' }}
                label={label}
                name={name}
                value={value ?? ''}
                type={type}
                onChange={handleChange}
                variant="standard"
            />
            ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
        variant='contained'
        color='error'
        onClick={() => setOpenDialog(true)}
        disabled={progress}
        sx={{ mt: 3 }}>
            Delete
        </Button>

        <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateCredit}
            disabled={progress}
            sx={{ mt: 3, }}
            >
                Update
        </Button>

{ openDialog && (
        <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            >
            <DialogTitle id="alert-dialog-title">
                {"Delete Credit"}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1">
                    Are you sure you want to delete this credit? This action cannot be undone.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenDialog(false)} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleDeleteCredit} color="error" autoFocus>
                    Delete
                </Button>   
            </DialogActions>
        </Dialog>
        )}
        </Box>
    </Box>
   
)
}

